import React, { useEffect } from "react";
import { EditorNavHeader } from "../components/common/EditorNavHeader";
import { NavigationBlocker } from "../components/common/NavigationBlocker";
import {
  useShippingEditorStore,
  selectShippingIsDirty,
  FREE_SHIPPING_RATE_ID,
} from "../store/useShippingEditorStore";
import {
  type ShippingRule,
  isPickupOfficesSupportedForShopCountry,
} from "@codway-dev/shared";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";
import { RateForm } from "../components/shipping/RateForm";
import type { CallbackEvent } from "@shopify/polaris-types";

// ---------- Helpers ----------

function formatPrice(rate: ShippingRule, _currency: string): string {
  if (rate.action.type === "free_shipping") return "0.00";
  if (rate.action.type === "flat_rate") return String(rate.action.cost);
  if (rate.action.type === "percentage_off")
    return `${rate.action.percentage}%`;
  return "—";
}

function conditionSummary(rate: ShippingRule): string {
  const cond = rate.conditions;
  if (!cond) return "Sin condiciones, siempre visible.";

  const parts: string[] = [];
  if (cond.min_subtotal != null) parts.push(`Min: ${cond.min_subtotal}`);
  if (cond.max_subtotal != null) parts.push(`Max: ${cond.max_subtotal}`);
  if (cond.allowed_state_codes && cond.allowed_state_codes.length > 0) {
    parts.push(`${cond.allowed_state_codes.length} estado(s)`);
  }

  return parts.length > 0
    ? parts.join(" · ")
    : "Sin condiciones, siempre visible.";
}

// ---------- Component ----------

const ShippingTaxesEditorPage: React.FC = () => {
  const rates = useShippingEditorStore((s) => s.rates);
  const currency = useShippingEditorStore((s) => s.currency);
  const countryCode = useShippingEditorStore((s) => s.countryCode);
  const expandedRateId = useShippingEditorStore((s) => s.expandedRateId);
  const isLoading = useShippingEditorStore((s) => s.isLoading);
  const isSaving = useShippingEditorStore((s) => s.isSaving);
  const error = useShippingEditorStore((s) => s.error);
  const isDirty = useShippingEditorStore(selectShippingIsDirty);
  const officesEnabled = useShippingEditorStore((s) => s.offices.enabled);
  const setOfficesEnabled = useShippingEditorStore((s) => s.setOfficesEnabled);
  const loadFromBackend = useShippingEditorStore((s) => s.loadFromBackend);
  const addRate = useShippingEditorStore((s) => s.addRate);
  const updateRate = useShippingEditorStore((s) => s.updateRate);
  const removeRate = useShippingEditorStore((s) => s.removeRate);
  const toggleExpanded = useShippingEditorStore((s) => s.toggleExpanded);
  const save = useShippingEditorStore((s) => s.save);
  const discard = useShippingEditorStore((s) => s.discardChanges);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  const handleImportPlaceholder = () => {
    const toast = window.shopify?.toast;
    if (toast) toast.show("Próximamente");
  };

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  const pickupOfficesAllowed =
    isPickupOfficesSupportedForShopCountry(countryCode);

  // --- Loading ---
  if (isLoading) {
    return (
      <div>
        <EditorNavHeader />
        <s-page heading="Tarifas de Envío">
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div>
        <EditorNavHeader />
        <s-page heading="Tarifas de Envío">
          <s-banner tone="critical">
            <s-text>Error al cargar: {error}</s-text>
          </s-banner>
        </s-page>
      </div>
    );
  }

  return (
    <div>
      <EditorNavHeader />
      <NavigationBlocker isDirty={isDirty} />
      <s-page heading="Tarifas de Envío">
        <s-section>
          <s-text>
            En esta sección puedes crear las tarifas de envío para tu
            formulario.{" "}
            <strong>
              Todos los precios se utilizarán en la divisa de tu tienda.
            </strong>
          </s-text>
        </s-section>
        <s-section>
          <s-stack direction="block" gap="base">
            <s-checkbox
              label="Puntos de recogida (pick-up)"
              checked={pickupOfficesAllowed ? officesEnabled : false}
              disabled={!pickupOfficesAllowed}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                setOfficesEnabled(e.currentTarget.checked)
              }
            />
            <s-text color="subdued">
              Permite que el formulario ofrezca &ldquo;Recoger en tienda&rdquo;
              como alternativa al envío a domicilio.
            </s-text>
            {!pickupOfficesAllowed && (
              <s-text color="subdued">
                Solo disponible para tiendas en Colombia (CO).
              </s-text>
            )}
          </s-stack>
        </s-section>
        <s-box padding="base" />
        <s-stack
          direction="inline"
          gap="base"
          alignItems="center"
          justifyContent="end"
        >
          <s-button variant="primary" onClick={addRate}>
            Añadir tarifa
          </s-button>
        </s-stack>
        <s-box padding="base" />

        {/* Rates table */}
        <s-section padding="none">
          <s-table>
            <s-table-header-row>
              <s-table-header>Nombre</s-table-header>
              <s-table-header>Precio</s-table-header>
              <s-table-header>Condición</s-table-header>
              <s-table-header>Acciones</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {rates.map((rate) => {
                const isFreeShipping = rate.id === FREE_SHIPPING_RATE_ID;
                return (
                  <s-table-row key={rate.id}>
                    <s-table-cell>{rate.name || "—"}</s-table-cell>
                    <s-table-cell>{formatPrice(rate, currency)}</s-table-cell>
                    <s-table-cell>{conditionSummary(rate)}</s-table-cell>
                    <s-table-cell>
                      <s-stack direction="inline" gap="small-200">
                        <s-button
                          variant="tertiary"
                          icon="edit"
                          onClick={() => toggleExpanded(rate.id!)}
                        />
                        {!isFreeShipping && (
                          <s-button
                            variant="tertiary"
                            icon="delete"
                            onClick={() => removeRate(rate.id!)}
                          />
                        )}
                      </s-stack>
                    </s-table-cell>
                  </s-table-row>
                );
              })}
            </s-table-body>
          </s-table>

          {rates.length === 0 && (
            <s-box padding="large-200">
              <s-text color="subdued">
                No hay tarifas de envío configuradas. Haz clic en &ldquo;Añadir
                tarifa&rdquo; para comenzar.
              </s-text>
            </s-box>
          )}
          {expandedRateId && (
            <>
              {rates
                .filter((r) => r.id === expandedRateId)
                .map((rate) => (
                  <RateForm
                    key={rate.id}
                    rate={rate}
                    currency={currency}
                    countryCode={countryCode}
                    canDelete={rate.id !== FREE_SHIPPING_RATE_ID}
                    onUpdate={updateRate}
                    onCollapse={() => toggleExpanded(rate.id!)}
                    onDelete={() => removeRate(rate.id!)}
                  />
                ))}
            </>
          )}
        </s-section>

        <s-box padding="base" />

        {/* Info banners */}
        <s-banner
          heading="¿Necesitas condiciones más específicas?"
          tone="info"
          dismissible
        >
          <s-stack direction="block" gap="base">
            Si necesitas personalizaciones más especializadas para tus tarifas
            de envío, no dudes en contactarnos. Estaremos encantados de
            incorporar las condiciones que necesites en tu formulario de pedido.
            Sin embargo, te pedimos amablemente que te abstengas de solicitar la
            creación de tarifas de envío estándar.
            <s-stack direction="inline" gap="base">
              {/* onClick on s-button inside s-banner doesn't reach React's event
                  system — wrap with a native element so the composed click event
                  is caught before the banner shadow DOM intercepts it. */}
              <div
                role="presentation"
                onClick={handleImportPlaceholder}
                style={{ display: "contents" }}
              >
                <s-button icon="chat">Contáctanos</s-button>
              </div>
            </s-stack>
          </s-stack>
        </s-banner>
        <s-banner tone="info" dismissible>
          <s-stack direction="block" gap="base">
            <strong>
              ¿Necesita tarifas diferentes según la Provincia / Estado?
            </strong>
            <s-text>
              Si necesita diferentes tarifas de envío según la provincia /
              estado, usa el tutorial a continuación. Si necesita ayuda, no dude
              en ponerse en contacto con nosotros, ¡nuestro equipo de soporte
              estará encantado de ayudarle!
            </s-text>
            <s-stack direction="inline" gap="base">
              <div
                role="presentation"
                onClick={handleImportPlaceholder}
                style={{ display: "contents" }}
              >
                <s-button icon="external">Abrir el tutorial</s-button>
              </div>
              <div
                role="presentation"
                onClick={handleImportPlaceholder}
                style={{ display: "contents" }}
              >
                <s-button icon="chat">Contáctanos</s-button>
              </div>
            </s-stack>
          </s-stack>
        </s-banner>
      </s-page>
    </div>
  );
};

export default ShippingTaxesEditorPage;
